import React, { FC, useCallback, useContext, useEffect } from 'react';
import { ReleasesFetcher } from '../../fetchers/ReleasesFetcher';
import { Link, useHistory, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { IProductReleaseForm, ProductReleaseForm } from './ProductReleaseForm';
import { useForm, FieldValues } from 'react-hook-form';
import { IInstaller } from '../../models/Installer';
import { DangerousButton } from '../CustomButtons/DangerousButton';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { apiProductRelease } from '../../resources/api/apiProductRelease';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { NotificationContext } from '../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../hooks/useDialog';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';

interface IUrlParams {
  productId: string;
  version?: string;
}

interface IProps {
  closeDialog: () => void;
}

const { useUpdateProductRelease, useDeleteProductRelease } = apiProductRelease;

export const EditReleaseDialog: FC<IProps> = ({ closeDialog }) => {
  const { data, onRefresh } = useContext(ReleasesFetcher.Context);
  const { version, productId } = useParams<IUrlParams>();
  const { control, handleSubmit, formState, watch, reset, trigger, getValues } = useForm<FieldValues>({
    mode: 'all',
    defaultValues: {
      changelogNotes: '',
      forceUpdate: false,
      pinCode: '',
      version: '',
      filePath: '',
    },
  });

  const isValid = formState.isValid;

  const { isOpen: isDelete, openUrl: deleteUrl, close: abortDelete } = useDialog('delete');

  const { execute: updateRelease } = useUpdateProductRelease(productId, version);
  const { execute: unRelease, Guard: GuardUnRelease } = useDeleteProductRelease(productId, version);

  const handleError = useApiErrorHandler();
  const history = useHistory();
  const { addNotification } = useContext(NotificationContext);
  const { t } = useTranslation();

  const onSubmit = useCallback(
    async (data: IProductReleaseForm) => {
      const payload = {
        ...data,
        pinCode: parseInt(data.pinCode),
      };
      try {
        await updateRelease(payload);
        await onRefresh();
        addNotification({
          severity: 'success',
          message: t('releases.editDialog.updated'),
        });
      } catch (e) {
        handleError(e);
      }
    },
    [updateRelease, onRefresh]
  );

  const allVersions = data?.releases[0]?.versions.reduce<IInstaller[]>(
    (memo, current) => [...memo, ...current.installer],
    []
  );

  const release = allVersions?.find((ver) => ver.version === version);
  const open = !!release;

  useEffect(() => {
    if (release)
      reset({
        changelogNotes: release.changelog,
        forceUpdate: release.forceUpdate,
        pinCode: '',
        version: release.version,
        filePath: release.filePath,
      });
  }, [release]);

  const onUnrelease = useCallback(async () => {
    const data = getValues();
    if (!data.pinCode) {
      await trigger('pinCode');
      history.push(AuthenticatedRoutes.ReleasesProductVersion(productId, version));
    } else {
      try {
        await unRelease(parseInt(data.pinCode));
        await onRefresh();
        closeDialog();
      } catch (e) {
        handleError(e);
      }
    }
  }, [unRelease, onRefresh]);

  if (!data || !release) return null;
  return (
    <>
      <Dialog open={open} onClose={closeDialog}>
        <DialogTitle>{t('releases.editDialog.title')}</DialogTitle>
        <DialogContent>
          <ProductReleaseForm
            onSubmit={onSubmit}
            control={control}
            handleSubmit={handleSubmit}
            formState={formState}
            watch={watch}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <GuardUnRelease>
            <DangerousButton component={Link} to={deleteUrl} disabled={!isValid} replace={true}>
              {t('releases.editDialog.unReleaseButton')}
            </DangerousButton>
          </GuardUnRelease>
          <Button color="primary" variant="contained" onClick={handleSubmit(onSubmit)} disabled={!isValid}>
            {t('releases.editDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={isDelete}
        title={t('releases.unReleaseDialog.title')}
        confirm={onUnrelease}
        abort={abortDelete}
        message={t('releases.unReleaseDialog.message')}
      />
    </>
  );
};
